import React, { useMemo } from 'react';
import { FormControl, Grid, FormLabel, IconButton, Tooltip } from '@mui/material';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useDispatch, useSelector } from 'react-redux';
import { InstantSearchFieldAdaptor } from '@/components/SearchField';
import { RefinementListSelect } from '@/components/selects/RefinementListSelect';
import { useLocation, useParams } from 'react-router-dom';
import { resetFilters, setFilters } from '@/features/table/instantSearchSlice';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { PresetDateRangeSelect } from '@/components/selects/PresetDateRangeSelect';
import { OwnerAutocomplete } from '@/components/selects/OwnerSelect';
import { endOfDay, startOfDay } from 'date-fns';
import { useInstantSearchDealOwnerFilters } from '@/hooks/useInstantSearchOwnerFilters';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import { useGridItemProps } from '@/components/table/filterGrid';
import { InformedConsentStatusSelect } from '@/components/selects/InformedConsentStatusSelect';

const DealTableFilters = ({
	pipeline,
	tab = 'active',
	spacing = 2,
	children,
	showInformedConsent = false,
}) => {
	const dispatch = useDispatch();
	const params = useParams();
	const isClientView = Boolean(params?.clientId);

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const { getFeatureFlags } = useFeatureFlagContext();
	const showNewBusinessKPI = Boolean(getFeatureFlags('newBusinessKPI')?.active);

	const isClaims = pipeline == process.env.REACT_APP_PIPELINE_CLAIMS;
	const isNewBusiness = pipeline == process.env.REACT_APP_PIPELINE_NEW_BUSINESS;
	const isRenewal = pipeline == process.env.REACT_APP_PIPELINE_RENEWALS;

	const userDetailsUserId = userDetails?.userId;
	const { kanbanView } = useSelector((state) => state.userPrefs);
	const showConsentFilter = !kanbanView && !isClaims && showInformedConsent;

	const { filters, owners, clientOwners, canViewGroup } = useInstantSearchDealOwnerFilters();
	// Users who can't view the group can only filter by themselves.
	const filterOwnerOptions = (user) => canViewGroup || user.id === userDetailsUserId;
	const { pathname: slug } = useLocation();

	const updateDateFilter = (fieldName, fromKey, toKey, fromDate, toDate) => {
		dispatch(
			setFilters({
				slug,
				type: fromKey,
				values: fromDate ? [fromDate.toISOString()] : [],
			}),
		);
		dispatch(
			setFilters({
				slug,
				type: toKey,
				values: toDate ? [toDate.toISOString()] : [],
			}),
		);

		if (!fromDate && !toDate) {
			dispatch(setFilters({ slug, type: fieldName, values: [] }));
			return;
		}

		let dateFilterString = '';
		if (fromDate && toDate) {
			const fromTimestamp = startOfDay(fromDate).getTime();
			const toTimestamp = endOfDay(toDate).getTime();
			dateFilterString = `${fieldName}:[${fromTimestamp}..${toTimestamp}]`;
		} else if (fromDate) {
			const fromTimestamp = startOfDay(fromDate).getTime();
			dateFilterString = `${fieldName}:>=${fromTimestamp}`;
		} else if (toDate) {
			const toTimestamp = endOfDay(toDate).getTime();
			dateFilterString = `${fieldName}:<=${toTimestamp}`;
		}

		if (dateFilterString) {
			dispatch(
				setFilters({
					slug,
					type: fieldName,
					values: [dateFilterString],
				}),
			);
		}
	};

	const updateRenewalDateFilter = (fromDate, toDate) => {
		dispatch(
			setFilters({
				slug,
				type: 'renewalDateFrom',
				values: fromDate ? [fromDate.toISOString()] : [],
			}),
		);
		dispatch(
			setFilters({
				slug,
				type: 'renewalDateTo',
				values: toDate ? [toDate.toISOString()] : [],
			}),
		);

		if (!fromDate && !toDate) {
			dispatch(
				setFilters({
					slug,
					type: 'renewalDate',
					values: [],
				}),
			);
			return;
		}

		let dateFilterString = '';
		if (fromDate && toDate) {
			const fromTimestamp = startOfDay(fromDate).getTime();
			const toTimestamp = endOfDay(toDate).getTime();
			dateFilterString = `renewalDate: [${fromTimestamp}..${toTimestamp}]`;
		} else if (fromDate) {
			const fromTimestamp = startOfDay(fromDate).getTime();
			dateFilterString = `renewalDate: >= ${fromTimestamp}`;
		} else if (toDate) {
			const toTimestamp = endOfDay(toDate).getTime();
			dateFilterString = `renewalDate: <= ${toTimestamp}`;
		}

		if (dateFilterString) {
			dispatch(
				setFilters({
					slug,
					type: 'renewalDate',
					values: [dateFilterString],
				}),
			);
		}
	};

	const createDateFrom = filters?.createDateFrom?.[0]
		? new Date(filters.createDateFrom[0])
		: null;
	const createDateTo = filters?.createDateTo?.[0] ? new Date(filters.createDateTo[0]) : null;
	const estimatedCloseDateFrom = filters?.estimatedCloseDateFrom?.[0]
		? new Date(filters.estimatedCloseDateFrom[0])
		: null;
	const estimatedCloseDateTo = filters?.estimatedCloseDateTo?.[0]
		? new Date(filters.estimatedCloseDateTo[0])
		: null;
	const fromDate = filters?.renewalDateFrom?.[0] ? new Date(filters.renewalDateFrom[0]) : null;
	const toDate = filters?.renewalDateTo?.[0] ? new Date(filters.renewalDateTo[0]) : null;

	const { searchGridProps, generalGridProps, clearFilterGridProps } = useGridItemProps();

	return (
		<Grid
			container
			spacing={1}
			sx={{
				width: '100%',
				pb: kanbanView ? 1 : 2,
			}}
		>
			<Grid container spacing={spacing} width='100%'>
				<Grid {...searchGridProps}>
					<FormControl fullWidth>
						<FormLabel>{'Search'}</FormLabel>
						<InstantSearchFieldAdaptor />
					</FormControl>
				</Grid>
				{!isClientView && (
					<>
						<Grid {...generalGridProps}>
							<FormControl fullWidth>
								<FormLabel>{'Assignee'}</FormLabel>
								<OwnerAutocomplete
									includeInactive
									multiple
									userId={owners}
									filterBy={filterOwnerOptions}
									// The user stays the assignee until a client owner is chosen
									isUserFixed={(user) =>
										user.id === userDetailsUserId &&
										owners.length === 1 &&
										clientOwners.length === 0
									}
									onChange={(value) => {
										dispatch(
											setFilters({
												slug,
												type: 'owners',
												values: [value].flat(),
											}),
										);
									}}
									returnValue='id'
									disableClearable={true}
								/>
							</FormControl>
						</Grid>

						<Grid {...generalGridProps}>
							<FormControl fullWidth>
								<FormLabel>{'Client owner'}</FormLabel>
								<OwnerAutocomplete
									includeInactive
									multiple
									userId={clientOwners}
									filterBy={filterOwnerOptions}
									onChange={(value) => {
										// Keep the assignee shown (usually the user, by default) once a
										// client owner is chosen; it's only dropped when the user removes it.
										if (clientOwners.length === 0) {
											dispatch(setFilters({ slug, type: 'owners', values: owners }));
										}
										dispatch(
											setFilters({
												slug,
												type: 'clientOwners',
												values: [value].flat(),
											}),
										);
									}}
									returnValue='id'
									disableClearable={true}
								/>
							</FormControl>
						</Grid>
					</>
				)}
				{isClaims && (
					<>
						<Grid {...generalGridProps}>
							<FormControl fullWidth>
								<FormLabel>Insurer</FormLabel>
								<RefinementListSelect
									attribute='insurer'
									pipeline={pipeline}
									multiple
									disableTooltip
								/>
							</FormControl>
						</Grid>
						{!isClientView && (
							<>
								<Grid {...generalGridProps}>
									<FormControl fullWidth>
										<FormLabel>Subject of loss</FormLabel>
										<RefinementListSelect
											attribute='claimType'
											pipeline={pipeline}
											multiple
											disableTooltip
										/>
									</FormControl>
								</Grid>

								<Grid {...generalGridProps}>
									<FormControl fullWidth>
										<FormLabel>Repairer</FormLabel>
										<RefinementListSelect
											attribute='repairer'
											pipeline={pipeline}
											multiple
											disableTooltip
										/>
									</FormControl>
								</Grid>
								<Grid {...generalGridProps}>
									<FormControl fullWidth>
										<FormLabel>Assessor</FormLabel>
										<RefinementListSelect
											attribute='assessor'
											pipeline={pipeline}
											multiple
											disableTooltip
										/>
									</FormControl>
								</Grid>
							</>
						)}
					</>
				)}
				{isNewBusiness && showNewBusinessKPI && (
					<>
						<Grid {...generalGridProps}>
							<FormControl fullWidth>
								<FormLabel>Created date</FormLabel>
								<PresetDateRangeSelect
									from={createDateFrom}
									to={createDateTo}
									onChange={(nextFrom, nextTo) =>
										updateDateFilter(
											'createDate',
											'createDateFrom',
											'createDateTo',
											nextFrom,
											nextTo,
										)
									}
								/>
							</FormControl>
						</Grid>

						<Grid {...generalGridProps}>
							<FormControl fullWidth>
								<FormLabel>Estimated close date</FormLabel>
								<PresetDateRangeSelect
									from={estimatedCloseDateFrom}
									to={estimatedCloseDateTo}
									onChange={(nextFrom, nextTo) =>
										updateDateFilter(
											'estimatedCloseDate',
											'estimatedCloseDateFrom',
											'estimatedCloseDateTo',
											nextFrom,
											nextTo,
										)
									}
								/>
							</FormControl>
						</Grid>
					</>
				)}

				{(isClientView || (!kanbanView && tab !== 'invoiced' && tab !== 'closed')) && (
					<Grid {...generalGridProps}>
						<FormControl fullWidth>
							<FormLabel>Stage</FormLabel>
							<RefinementListSelect
								attribute='dealStage'
								pipeline={pipeline}
								tab={tab}
								multiple
								disableTooltip
							/>
						</FormControl>
					</Grid>
				)}
				{showConsentFilter && (
					<Grid {...generalGridProps}>
						<FormControl fullWidth>
							<FormLabel>Informed Consent</FormLabel>
							<InformedConsentStatusSelect />
						</FormControl>
					</Grid>
				)}
				{(!pipeline || isRenewal) && (
					<Grid {...generalGridProps}>
						<FormControl fullWidth>
							<FormLabel>End date</FormLabel>
							<PresetDateRangeSelect
								from={fromDate}
								to={toDate}
								onChange={(nextFrom, nextTo) =>
									updateRenewalDateFilter(nextFrom, nextTo)
								}
							/>
						</FormControl>
					</Grid>
				)}
				{!kanbanView && (
					<Grid {...clearFilterGridProps}>
						<Tooltip title='Clear filters'>
							<IconButton
								// sx={{ width: '100%', minHeight: '40px', borderRadius: '6px' }}
								variant='text'
								size='small'
								color='primary'
								onClick={() => dispatch(resetFilters(slug))}
							>
								<FilterAltOffOutlinedIcon />
							</IconButton>
						</Tooltip>
					</Grid>
				)}
				{children}
			</Grid>
		</Grid>
	);
};

DealTableFilters.displayName = 'DealTableFilters';
export default DealTableFilters;
